import ErrorBoundary from "@/core/error/ErrorBoundary"
import makeUseStyles from "@/core/ui/style/util/makeUseStyles"
import CommunityNonActiveStandingPage from "@/main/page/CommunityNonActiveStandingPage"
import { HeaderContentProps } from "@/main/page/header/HeaderContent"
import { DiscoAlert, DiscoAlertProps, DiscoIconKindsReactNodeUnion } from "@disco-ui"
import DiscoDelay from "@disco-ui/delay/DiscoDelay"
import DiscoPage from "@disco-ui/page/DiscoPage"
import { useTheme } from "@material-ui/core/styles"
import { Skeleton } from "@material-ui/lab"
import useSubscriptionStanding from "@utils/hook/useSubscriptionStanding"
import { sluggifyText } from "@utils/string/stringUtils"
import { Suspense } from "react"

export interface AdminConsolePageProps
  extends Pick<HeaderContentProps, "rightTitleContent"> {
  children: React.ReactNode
  title?: string
  ctaButton?: React.ReactNode
  subtabs?: React.ReactNode
  checkPlatformAccess?: boolean
  icon?: DiscoIconKindsReactNodeUnion
  infoBannerProps?: Omit<DiscoAlertProps, "title"> & { title: string }
}

function AdminConsolePage(props: AdminConsolePageProps) {
  const {
    children,
    title,
    rightTitleContent,
    icon,
    ctaButton,
    subtabs,
    infoBannerProps,
    checkPlatformAccess = true,
  } = props
  const theme = useTheme()
  const classes = useStyles()

  const subscriptionStanding = useSubscriptionStanding()
  if (checkPlatformAccess && subscriptionStanding !== "active") {
    return <CommunityNonActiveStandingPage standing={subscriptionStanding} />
  }

  return (
    <DiscoPage
      HeaderProps={{ title, rightTitleContent, leftIcon: icon, rightOptions: ctaButton }}
      subtabs={subtabs && <div className={classes.subtabsContainer}>{subtabs}</div>}
      contentClasses={{ productTabContent: classes.adminDiscoPage }}
    >
      <Suspense fallback={<DiscoDelay component={TabContentSkeleton} />}>
        <ErrorBoundary>
          {infoBannerProps?.title && infoBannerProps?.message && (
            <DiscoAlert
              marginBottom={1.5}
              gradientBackground={theme.palette.gradient.blue}
              dismissibleKey={`admin-guide-${sluggifyText(infoBannerProps.title)}`}
              data-testid={`AdminConsolePage.banner.${infoBannerProps.title}`}
              {...infoBannerProps}
            />
          )}
          {children}
        </ErrorBoundary>
      </Suspense>
    </DiscoPage>
  )
}

const useStyles = makeUseStyles((theme) => ({
  subtabsContainer: {
    marginBottom: theme.spacing(3),
  },
  adminDiscoPage: {
    marginBottom: theme.spacing(10),
  },
}))

export default AdminConsolePage

export const TabContentSkeleton = () => {
  const theme = useTheme()

  // Random blocks to take up space
  return (
    <Skeleton
      variant={"rect"}
      style={{
        borderRadius: theme.measure.borderRadius.big,
        height: "400px",
      }}
    />
  )
}
